// FILE: src/components/QuestionFactory/EditDraftModal.js
//
// Phase 3 — lightweight JSON editor for a Question Factory draft.
// Lets the admin tweak the raw_payload before approving. Validates that
// the input is valid JSON before calling edit_pipeline_item.
//
// Phase 4 may swap this for a full QuestionEditor integration that
// uses the same form fields the editor uses for live questions.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { editItem } from '../../services/questionFactoryService';

const STYLES = {
  backdrop: {
    position: 'fixed', inset: 0,
    background: 'rgba(0, 0, 0, 0.65)',
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    padding: '32px 16px', overflow: 'auto', zIndex: 1100,
  },
  shell: {
    background: '#15152a', border: '1px solid #2a2a4a', borderRadius: '8px',
    width: '100%', maxWidth: '860px', color: '#eee',
    padding: '24px 28px', boxSizing: 'border-box',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  header: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' },
  title:  { fontSize: '1.05rem', fontWeight: 600, flex: 1 },
  closeBtn: {
    background: 'transparent', border: '1px solid #555', color: '#ccc',
    borderRadius: '4px', padding: '4px 12px', cursor: 'pointer', fontSize: '0.85rem',
  },
  textarea: {
    width: '100%', minHeight: '420px',
    background: '#0f0f1e', border: '1px solid #2a2a4a', borderRadius: '6px',
    color: '#eee', padding: '12px 14px', fontFamily: 'Menlo, Consolas, monospace',
    fontSize: '0.8rem', boxSizing: 'border-box', resize: 'vertical',
  },
  noteInput: {
    width: '100%', background: '#0f0f1e', border: '1px solid #2a2a4a',
    borderRadius: '4px', color: '#eee', padding: '8px 10px', boxSizing: 'border-box',
    fontFamily: 'inherit', fontSize: '0.85rem', marginTop: '12px',
  },
  buttonRow: { display: 'flex', gap: '10px', marginTop: '14px', justifyContent: 'flex-end' },
  saveBtn: { padding: '8px 16px', background: '#3a3a6e', border: '1px solid #5a5a9e',
             borderRadius: '6px', color: '#fff', cursor: 'pointer', fontSize: '0.85rem' },
  cancelBtn: { padding: '8px 16px', background: 'transparent', border: '1px solid #555',
               borderRadius: '6px', color: '#ccc', cursor: 'pointer', fontSize: '0.85rem' },
  error: { color: '#ff6b6b', fontSize: '0.85rem', marginTop: '8px' },
  hint:  { fontSize: '0.75rem', color: '#888', marginBottom: '8px' },
};

export default function EditDraftModal({ item, onClose, onSaved }) {
  const initial = useMemo(() => JSON.stringify(item?.raw_payload || {}, null, 2), [item]);
  const [text, setText]     = useState(initial);
  const [note, setNote]     = useState('');
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState(null);

  useEffect(() => { setText(initial); setNote(''); setError(null); }, [initial]);

  const handleBackdrop = useCallback((e) => {
    if (e.target === e.currentTarget && !busy && typeof onClose === 'function') onClose();
  }, [busy, onClose]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy && typeof onClose === 'function') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const save = useCallback(async () => {
    setError(null);
    let parsed;
    try {
      parsed = JSON.parse(text);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('Payload must be a JSON object.');
      }
    } catch (e) {
      setError(`Invalid JSON: ${e.message}`);
      return;
    }
    setBusy(true);
    try {
      const updated = await editItem({ itemId: item.id, payload: parsed, note: note || null });
      if (typeof onSaved === 'function') onSaved(updated);
      onClose();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }, [text, note, item, onSaved, onClose]);

  if (!item) return null;

  return (
    <div style={STYLES.backdrop} onClick={handleBackdrop} data-testid="qf-edit-modal">
      <div style={STYLES.shell} role="dialog" aria-label="Edit draft">
        <div style={STYLES.header}>
          <div style={STYLES.title}>Edit draft {item.id?.slice(0, 8)}…</div>
          <button data-testid="qf-edit-modal-close" style={STYLES.closeBtn} onClick={onClose} disabled={busy}>Close</button>
        </div>
        <div style={STYLES.hint}>
          Edit the raw question payload JSON. The shape must match the canonical question contract: <code>{'{ id, question, choices, correctAnswers, explanation, metadata: {...} }'}</code>. Re-validation runs the next time this item is published.
        </div>
        <textarea data-testid="qf-edit-textarea" style={STYLES.textarea}
                  value={text} onChange={(e) => setText(e.target.value)} disabled={busy} />
        <input data-testid="qf-edit-note" style={STYLES.noteInput} type="text"
               placeholder="Admin note (optional)"
               value={note} onChange={(e) => setNote(e.target.value)} disabled={busy} />
        {error && <div style={STYLES.error} data-testid="qf-edit-error">{error}</div>}
        <div style={STYLES.buttonRow}>
          <button data-testid="qf-edit-cancel" style={STYLES.cancelBtn} onClick={onClose} disabled={busy}>Cancel</button>
          <button data-testid="qf-edit-save"   style={STYLES.saveBtn}   onClick={save}    disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
